import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit'
import { formatDate } from '@view/hooks/useDateTime'
import { ResBusinessDefaultsType } from '../../contexts/FodContainerContext/types'
import { fetchBusinessDefaults, fetchIndexDefaults } from './thunks'
import { setBespokeMandatoryFields } from './resUiSlice'

const initialState: ResBusinessDefaultsType = {
	accountNumber: '',
	additionalDriverLimit: null,
	additionalEmailAddressToRecieveConfirmation: [],
	address: '',
	approverValidationType: '',
	autoAllocate: false,
	bespokeFieldInfo: [],
	bookerConfirmationShowSalesQuote: 0,
	bookerRecievesEmail: false,
	bookerRecievesSMS: false,
	businessVehicleCodes: {},
	businessVehicleCodesReact: {},
	businessVehicleDefault: null,
	businessVehicleDefaults: [],
	canBookInPast: false,
	canUseUnregisteredDrivers: false,
	countryId: '',
	cuiUserSelectTariff: false,
	currentTariffBandBreakdowns: [],
	currencySymbol: '£',
	customResRules: '',
	customResRulesName: null,
	defaultDriverStatusRentalVehicleGroupRestrictions: [],
	defaultEndDateDays: 0,
	disallowDeliveryAndCollection: false,
	driverLineManagerEmailAddressMandatory: false,
	driverReceivesEmail: false,
	driverReceivesSMS: false,
	enforceInsuranceType: '',
	enforcecostCentre: '',
	gracePeriodForCustomer: '',
	hasApprovalGrades: false,
	hideCUIEditLineManager: false,
	hideLineManager: false,
	hideSupplierFromCUI: false,
	insuranceExpiryDate: '',
	insuranceExpiryTimestamp: '',
	insuranceType: [],
	limitDriverAddresses: 0,
	limitToExistingDrivers: false,
	lineManagerRequired: false,
	lockStartDate: false,
	minimumEndDateDays: 0,
	notes: null,
	orderNumberFormat: '',
	pickupType: '',
	provecta: false,
	reasonsForHire: [],
	reasonForHireRequired: false,
	fetchBusinessDefaultsLoading: false,
	showBookerNameOnInvoice: false,
	specialInstructions: '',
	supplierInstructions: '',
	telephoneNumber: '',
	defaultDriverBusinessBespokeFields: null,
	isQuotesUnavailable: false,
}

export const businessDefaultsSlice = createSlice({
	name: 'businessDefaults',
	initialState,
	reducers: {
		setSpecialInstructions(state, action) {
			state.specialInstructions = action.payload
		},
		setBusinessVehicleDefaults(state, action) {
			state.businessVehicleCodesReact = action.payload
		},
		setLocationDefaults(state, action) {
			const {
				reasonsForHire,
				pickUpType,
				minimumEndDateDays,
				defaultEndDateDays,
				currentTariffBandBreakdowns,
				canBookInPast,
				reasonForHireRequired,
			} = action.payload

			state.reasonsForHire = reasonsForHire
			state.pickupType = pickUpType
			state.minimumEndDateDays = minimumEndDateDays
			state.defaultEndDateDays = defaultEndDateDays
			state.currentTariffBandBreakdowns = currentTariffBandBreakdowns
			state.canBookInPast = canBookInPast
			state.reasonForHireRequired = reasonForHireRequired
		},
		setOrderDetailsDefaults(state, action) {
			const newState = { ...state }

			const { insuranceExpiryDate, bookerConfirmationShowSalesQuote, insuranceTypeOptions, ...otherProps } = action.payload

			const insuranceTypeOptionsFormatted: ResBusinessDefaultsType['insuranceTypeOptions'] = Object.values(insuranceTypeOptions).map(
				value => ({
					key: value,
					text: value,
					value,
				})
			)

			const permissionKeyList = [
				'enforceInsuranceType',
				'hideCustomerOrderNumber',
				'hideCostCentre',
				'hidePurchaseOrderNumber',
				'bookerConfirmationShowSalesQuote',
				'enforcecostCentre',
			]

			newState.insuranceExpiryDate = formatDate(new Date(insuranceExpiryDate))
			newState.bookerConfirmationShowSalesQuote = Number(bookerConfirmationShowSalesQuote)
			newState.insuranceTypeOptions = insuranceTypeOptionsFormatted

			return { ...newState, ...checkPermissionsValuesPresent(permissionKeyList, action.payload), ...otherProps }
		},
		setDefaultDriverBusinessBespokeFields(state, action) {
			state.defaultDriverBusinessBespokeFields = action.payload
		},
	},
	extraReducers: builder => {
		builder.addCase(setBespokeMandatoryFields, (state, action) => {
			state.bespokeFieldInfo = action.payload
		})
		builder.addCase(fetchIndexDefaults.pending, (state, _action) => {
			state.fetchBusinessDefaultsLoading = true
		})
		builder.addCase(fetchIndexDefaults.fulfilled, (state, action: PayloadAction<ResBusinessDefaultsType, string>) => {
			state.fetchBusinessDefaultsLoading = false
			const { specialInstructions } = action.payload
			state.specialInstructions = specialInstructions
		})
		builder.addCase(fetchBusinessDefaults.pending, (state, _action) => {
			state.fetchBusinessDefaultsLoading = true
		})
		builder.addCase(fetchBusinessDefaults.fulfilled, (_state, action: PayloadAction<ResBusinessDefaultsType, string>) => {
			let newState = action.payload
			const { businessVehicleCodesReact, businessVehicleDefaults, selectableGroups, insuranceTypeOptions = {} } = action.payload
			const { insuranceExpiryDate } = newState
			const defaultVehiclesToShow = Array.isArray(selectableGroups) ? businessVehicleDefaults : selectableGroups

			let { reasonsForHire } = newState
			newState.fetchBusinessDefaultsLoading = false
			if (Array.isArray(reasonsForHire) && reasonsForHire.length) {
				reasonsForHire = reasonsForHire.reduce((accum, val) => {
					const newObj = {
						text: '',
						value: '',
						key: '',
						description: '',
					}
					newObj.text = val.reason
					newObj.value = val.id
					newObj.key = val.id
					if (val.archivedDateTime) {
						newObj.description = val.archivedDateTime
					}
					accum = [...accum, newObj]
					return accum
				}, [])
			} else {
				reasonsForHire = Object.entries(reasonsForHire).reduce((accum, [key, values]) => {
					accum = [...accum, { text: [key], value: values.id }]
					return accum
				}, [])
			}
			newState.insuranceExpiryDate = formatDate(new Date(insuranceExpiryDate))
			newState.reasonsForHire = reasonsForHire

			newState.businessVehicleCodesReact = businessVehiclesBasedOnBusinessDefaults(businessVehicleCodesReact, defaultVehiclesToShow)
			newState.bookerConfirmationShowSalesQuote = Number(newState.bookerConfirmationShowSalesQuote)

			const permissionKeyList = [
				'enforceInsuranceType',
				'hideCustomerOrderNumber',
				'hideCostCentre',
				'hidePurchaseOrderNumber',
				'bookerConfirmationShowSalesQuote',
				'enforcecostCentre',
			]

			const insuranceTypeOptionsFormatted: ResBusinessDefaultsType['insuranceTypeOptions'] = Object.values(insuranceTypeOptions).map(
				value => ({
					key: value,
					text: value,
					value,
				})
			)

			newState = {
				...newState,
				...checkPermissionsValuesPresent(permissionKeyList, action.payload),
				insuranceTypeOptions: insuranceTypeOptionsFormatted,
			}

			return newState
		})
	},
})

export const businessDefaultsSliceReducer = businessDefaultsSlice.reducer

export const selectBusinessDefaults = (state: RootState) => state.resbusinessDefault

export const selectVehicleCodeReact = createSelector([selectBusinessDefaults], state => {
	return state.businessVehicleCodesReact
})

export const {
	setBusinessVehicleDefaults,
	setLocationDefaults,
	setOrderDetailsDefaults,
	setSpecialInstructions,
	setDefaultDriverBusinessBespokeFields,
} = businessDefaultsSlice.actions

function checkPermissionsValuesPresent(keyList: string[], permissions: ResBusinessDefaultsType) {
	return keyList.reduce((accum, key) => {
		accum = {
			...accum,
			[key]: !permissions[key as keyof ResBusinessDefaultsType] ? null : permissions[key as keyof ResBusinessDefaultsType],
		}
		return accum
	}, {})
}

export function businessVehiclesBasedOnBusinessDefaults(
	currentVehicles: ResBusinessDefaultsType['businessVehicleCodesReact'],
	businessDefaults: ResBusinessDefaultsType['businessVehicleDefaults'] | ResBusinessDefaultsType['selectableGroups'] = {}
) {
	const filteredBusinessVehicles: ResBusinessDefaultsType['businessVehicleCodesReact'] = {}

	for (const [vehicleType, vehicleList] of Object.entries(currentVehicles)) {
		const vehicleListFilteredByBusinessDefaultsCodes = vehicleList.filter(function matchVehicleIdWithBusinessDefaultsCodes(vehicle) {
			return Object.keys(businessDefaults).some(vehicleCode => vehicleCode === vehicle.id)
		})

		filteredBusinessVehicles[vehicleType] = vehicleListFilteredByBusinessDefaultsCodes
	}

	return filteredBusinessVehicles
}
